import * as React from "react";

import DocEditHeaderSelectInput from "../../EditBar/EditHeader/SelectInput";
import DocStyledText from "../../BlockNodeHelpers/StyledText";

import {
  REFERENCE_RANGE_LAB_NAMES,
  REFERENCE_RANGE_INTERVAL_LOOKUP,
  RESULT_STATUS_TITLE_ARRAY,
  RESULT_STATUS_OPTIONS_OBJECT,
  FIRST_LAB_VALUE,
  FIRST_LAB_VALUE_RANGE,
  FIRST_RESULT_STATUS,
} from "./constants";
import {
  newDeleteNodeChange,
  newSetKeyValueChange,
} from "../../../../helpers/Doc/changes";
import {
  getAlertStyle,
  getLabValuesTableRowDeleteUiState,
} from "./utils";
import { getHighlightStyle } from "../../../../helpers/utils";

interface LabValuesTableRowProps extends BlockNodeAll {
  row: DocLabValuesTableRowType<InlineNode>;
  rowIndex: number;
  parentTableUID: string;
  uid: string;
  blockChangeHighlight?: BlockChangeHighlight;
}

const LabValuesTableRow: React.FC<LabValuesTableRowProps> = (props) => {
  const { row, editingContext, uid, blockChangeHighlight } = props;
  const labValue = row.labValue || FIRST_LAB_VALUE;
  const resultStatusKey = row.resultStatus || FIRST_RESULT_STATUS;
  const resultStatus = RESULT_STATUS_OPTIONS_OBJECT[resultStatusKey];

  const rangesArray = Object.entries(
    REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges
  );
  const referenceRangeIntervalOptions = rangesArray.map((range) => {
    return { label: range[0], value: range[0] };
  });
  const validRangeIntervals = Object.keys(REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges);
  const rangeIntervalOption =
    validRangeIntervals.includes(row.referenceRangeInterval)
      ? row.referenceRangeInterval
      : validRangeIntervals[0];
  const referenceRangeInterval =
    REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges[rangeIntervalOption] ||
    FIRST_LAB_VALUE_RANGE ||
    "";

  const isLabValueOther = labValue === "other";

  const getLabValue = () => {
    return (
      REFERENCE_RANGE_LAB_NAMES.find((item) => item.value === labValue)
        ?.label || ""
    );
  };

  const { isDelete, disableDeleteButton } = getLabValuesTableRowDeleteUiState(
    row,
    blockChangeHighlight
  );
  const strikeStyle =
    isDelete
      ? {
          textDecoration: "line-through",
          textDecorationColor: `rgb(${
            editingContext.colorLookup[blockChangeHighlight.author_id]
          })`,
        }
      : {};

  function renderLabValueCellContent() {
    const highlightStyle = getHighlightStyle({ blockChangeHighlight, editingContext });
    if (isLabValueOther) {
      const docStyledTextProps = {
        uid,
        content: row.labValueOther ?? "",
        contentKey: "labValueOther",
        placeholder: "Enter lab value",
        editingContext,
        independentlyEditable: true,
        readOnly: !editingContext.isEditing || isDelete,
      };
      const docStyledText = <DocStyledText {...docStyledTextProps} />;
      if (editingContext.isEditing) {
        return (
          <div className="lab-values-table-other-margin">
            <span style={highlightStyle}>{docStyledText}</span>
          </div>
        );
      }
      return <span style={highlightStyle}>{docStyledText}</span>;
    }
    if (!isLabValueOther && !editingContext.isEditing) {
      return <span style={highlightStyle}>{getLabValue()}</span>;
    }
    return null;
  }

  const labValueCell = (
    <td
      key="lab-select"
      className="lab-values-table-body-cell"
      aria-label="Lab value cell"
    >
      {editingContext.isEditing && (
        <DocEditHeaderSelectInput
          contentKey={"labValue"}
          defaultValue={FIRST_LAB_VALUE}
          disableMetadataEdit={isDelete}
          editingContext={editingContext}
          label={""}
          options={REFERENCE_RANGE_LAB_NAMES}
          uid={uid}
          value={labValue}
          useMenuPortal
          classNamePrefix="lab-values-table-select"
        />
      )}
      {renderLabValueCellContent()}
    </td>
  );

  // 2nd column: DocParagraph in editing mode, styled display otherwise
  const resultCell = (
    <td
      key="result-value"
      className={`lab-values-table-body-cell${
        resultStatusKey !== "normal" ? " lab-values-table-body-cell-bg" : ""
      }`}
      aria-label="Result cell"
    >
      <div className="lab-values-table-result-container">
        <span style={getAlertStyle(resultStatus?.isAlert)}>
          <span
            style={getHighlightStyle({ blockChangeHighlight, editingContext })}
          >
            <DocStyledText
              uid={uid}
              content={row.result ?? ""}
              contentKey={"result"}
              placeholder={"Enter result here"}
              editingContext={editingContext}
              independentlyEditable
              readOnly={isDelete}
            />
          </span>{" "}
          {resultStatus?.symbol || ""}
        </span>
      </div>
    </td>
  );

  // 3rd column: select input in editing mode only
  const resultStatusCell = editingContext.isEditing ? (
    <td
      key="result-status-select"
      className="lab-values-table-body-cell"
      aria-label="Result status cell"
    >
      <DocEditHeaderSelectInput
        contentKey={"resultStatus"}
        defaultValue={FIRST_RESULT_STATUS}
        disableMetadataEdit={isDelete}
        editingContext={editingContext}
        label={""}
        options={RESULT_STATUS_TITLE_ARRAY}
        uid={uid}
        value={resultStatusKey}
        useMenuPortal
        classNamePrefix="lab-values-table-select"
      />
    </td>
  ) : null;

  // 4th column: Reference Range Interval input or select (editing only)
  const referenceRangeIntervalCell = editingContext.isEditing ? (
    <td
      key={`reference-range-interval-select-${row.referenceRangeInterval}`}
      className="lab-values-table-body-cell"
      aria-label="Reference range interval cell"
    >
      {isLabValueOther ? (
        <div className="lab-values-table-other-margin">
          <DocStyledText
            uid={uid}
            content={row.referenceRangeIntervalOther ?? ""}
            contentKey={"referenceRangeIntervalOther"}
            placeholder={"Enter reference range interval"}
            editingContext={editingContext}
            independentlyEditable
          />
        </div>
      ) : (
        <div>
          <DocEditHeaderSelectInput
            contentKey={"referenceRangeInterval"}
            defaultValue={rangeIntervalOption}
            disableMetadataEdit={isDelete}
            editingContext={editingContext}
            label={""}
            options={referenceRangeIntervalOptions}
            uid={uid}
            value={rangeIntervalOption}
            useMenuPortal
            classNamePrefix="lab-values-table-select"
          />
        </div>
      )}
    </td>
  ) : null;

  // Reference Range column: always display the Reference Range Interval value before the Reference Range value
  const referenceRangeCell = (
    <td
      key="reference-range-value"
      className="lab-values-table-body-cell"
      aria-label="Reference range cell"
    >
      {isLabValueOther ? (
        <div className="lab-values-table-reference-range-container">
          <span>
            <DocStyledText
              uid={uid}
              content={row.referenceRangeOther ?? ""}
              contentKey={"referenceRangeOther"}
              placeholder={"Enter reference range"}
              editingContext={editingContext}
              independentlyEditable
              readOnly={isDelete}
            />
          </span>
        </div>
      ) : (
        <span
          style={getHighlightStyle({ blockChangeHighlight, editingContext })}
          dangerouslySetInnerHTML={{__html: referenceRangeInterval}}
        />
      )}
    </td>
  );

  function updateIntervalAndRangeValue() {
    if (
      editingContext.isEditing &&
      (!row.referenceRangeInterval ||
        !(row.referenceRangeInterval in REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges))
    ) {
      const referenceRangeInterval =
        Object.keys(REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges)[0] || "";
      editingContext.addChange(
        newSetKeyValueChange(
          uid,
          "referenceRangeInterval",
          referenceRangeInterval
        )
      );
      const referenceRange =
        REFERENCE_RANGE_INTERVAL_LOOKUP[labValue]?.ranges?.[
          referenceRangeInterval
        ] || "";
      editingContext.addChange(
        newSetKeyValueChange(uid, "referenceRange", referenceRange)
      );
    }
  }

  React.useEffect(() => {
    updateIntervalAndRangeValue();
  }, [row.labValue]);

  React.useEffect(() => {
    if (editingContext.isEditing && !row.labValue) {
      editingContext.addChange(
        newSetKeyValueChange(uid, "labValue", FIRST_LAB_VALUE)
      );
      editingContext.addChange(
        newSetKeyValueChange(uid, "resultStatus", FIRST_RESULT_STATUS)
      );
    }
  }, [editingContext.isEditing, uid]);

  // Update reference range value when reference range interval changes
  React.useEffect(() => {
    if (
      editingContext.isEditing &&
      row.referenceRangeInterval &&
      labValue !== "other" &&
      row.referenceRangeInterval in REFERENCE_RANGE_INTERVAL_LOOKUP[labValue].ranges
    ) {
      const newReferenceRange =
        REFERENCE_RANGE_INTERVAL_LOOKUP[labValue]?.ranges?.[row.referenceRangeInterval] || "";
      if (row.referenceRange !== newReferenceRange) {
        editingContext.addChange(
          newSetKeyValueChange(uid, "referenceRange", newReferenceRange)
        );
      }
    }
  }, [row.referenceRangeInterval, labValue, editingContext.isEditing]);

  // Add a delete button at the end of each row in editing mode
  const deleteRowCell = editingContext.isEditing ? (
    <td
      key="lab-values-table-remove-row-container"
      aria-label="Delete row cell"
    >
      <div className="lab-values-table-remove-button-container">
        <button
          type="button"
          onClick={() => {
            if (disableDeleteButton) {
              return;
            }
            editingContext.clearCurrentDocSelection();
            editingContext.addChange(newDeleteNodeChange(uid));
          }}
          className={`lab-values-table-remove-table-button lab-values-table-remove-row-button no-document-selection ${
            disableDeleteButton ? "strikeout" : ""
          }`}
          disabled={disableDeleteButton}
          style={disableDeleteButton ? strikeStyle : {}}
          aria-label="Delete row"
        >
          <i className="fa fa-times" />
        </button>
      </div>
    </td>
  ) : null;

  // Only apply highlight style to the row in editing mode
  const rowStyle = editingContext.isEditing
    ? getHighlightStyle({ blockChangeHighlight, editingContext, forRow: true })
    : {};

  return (
    <tr
      key={uid}
      id={uid}
      data-is-node
      data-uid={uid}
      className="lab-values-table-body-row"
      style={rowStyle}
    >
      {labValueCell}
      {resultCell}
      {editingContext.isEditing && resultStatusCell}
      {editingContext.isEditing && referenceRangeIntervalCell}
      {referenceRangeCell}
      {deleteRowCell}
    </tr>
  );
};

export default LabValuesTableRow;
